import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Alert,
  TextField,
  MenuItem,
  Chip,
} from "@mui/material";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { getClientSignals } from "../../../utils/clientSignals";
import { trackMetaEvent, trackMetaPurchase } from "../../../utils/metaPixel";
import { registrarSalesEvent } from "../../../utils/salesEvents";

const onlyDigits = (value) => String(value || "").replace(/\D/g, "");

const formatCpf = (value) =>
  onlyDigits(value)
    .slice(0, 11)
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d{1,2})$/, "$1-$2");

const formatCardNumber = (value) =>
  onlyDigits(value)
    .slice(0, 19)
    .replace(/(.{4})/g, "$1 ")
    .trim();

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

const isValidCpf = (value) => {
  const cpf = onlyDigits(value);
  if (cpf.length !== 11 || /^(\d)\1{10}$/.test(cpf)) return false;

  const calculateDigit = (base) => {
    let factor = base.length + 1;
    const total = base
      .split("")
      .reduce((sum, digit) => sum + Number(digit) * factor--, 0);
    const rest = (total * 10) % 11;
    return rest === 10 ? 0 : rest;
  };

  const digit1 = calculateDigit(cpf.slice(0, 9));
  const digit2 = calculateDigit(cpf.slice(0, 10));

  return digit1 === Number(cpf[9]) && digit2 === Number(cpf[10]);
};

const PagamentoCartao = ({
  userId,
  onCancel,
  valorPagamento,
  valorParcela2x = null,
  initialBuyer = {},
  onStageChange,
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState("");
  const [erroCep, setErroCep] = useState("");
  const trackedCheckoutPaymentIdRef = useRef(null);
  const formStartedRef = useRef(false);

  const [dados, setDados] = useState({
    nome: initialBuyer.fullName || "",
    email: initialBuyer.email || "",
    cpf: "",
    telefone: onlyDigits(initialBuyer.phone || "").slice(0, 11),
    numeroEndereco: "",
    cartaoNumero: "",
    cartaoNome: "",
    validadeMes: "",
    validadeAno: "",
    ccv: "",
    parcelas: 1,
  });

  const formatCurrencyBRL = (value) =>
    Number(value || 0).toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });
  const getInstallmentAmount = (installments) => {
    if (Number(installments) === 2 && Number(valorParcela2x) > 0) {
      return Number(valorParcela2x);
    }

    return (valorPagamento || 299) / installments;
  };

  // 🔹 Captura userId do localStorage se não vier por prop
  useEffect(() => {
    if (!userId) {
      try {
        const storedUser = JSON.parse(localStorage.getItem("user"));
        if (storedUser?.id) setDados((prev) => ({ ...prev, userId: storedUser.id }));
      } catch {
        console.warn("⚠️ Não foi possível recuperar o userId localmente.");
      }
    }
  }, [userId]);

  useEffect(() => {
    setDados((current) => ({
      ...current,
      nome: current.nome || initialBuyer.fullName || "",
      email: current.email || initialBuyer.email || "",
      telefone:
        current.telefone || onlyDigits(initialBuyer.phone || "").slice(0, 11),
    }));
  }, [initialBuyer.email, initialBuyer.fullName, initialBuyer.phone]);

  const markFormStarted = (firstField) => {
    if (formStartedRef.current) return;
    formStartedRef.current = true;
    onStageChange?.("form_started");
    registrarSalesEvent(userId, "payment_form_started", {
      source: "payment_form",
      metadata: { method: "card", first_field: firstField },
    });
  };

  const failValidation = (field, code, message, { cep = false } = {}) => {
    if (cep) setErroCep(message);
    else setErro(message);
    setLoading(false);
    registrarSalesEvent(userId, "payment_validation_error", {
      source: "payment_form",
      metadata: { method: "card", field, code },
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const normalizers = {
      cpf: formatCpf,
      telefone: (input) => onlyDigits(input).slice(0, 11),
      cartaoNumero: formatCardNumber,
      validadeMes: (input) => onlyDigits(input).slice(0, 2),
      validadeAno: (input) => onlyDigits(input).slice(0, 4),
      ccv: (input) => onlyDigits(input).slice(0, 4),
    };

    setDados((prev) => ({
      ...prev,
      [name]: normalizers[name] ? normalizers[name](value) : value,
    }));
  };

  const pagar = async () => {
    markFormStarted("submit");
    setErro("");
    setLoading(true);

    const cep = dados.cep?.trim();
    const cepValido = /^[0-9]{8}$/.test(cep || "");
    const numeroEndereco = (dados.numeroEndereco || "").trim();
    const nome = (dados.nome || "").trim();
    const email = (dados.email || "").trim();
    const cpf = onlyDigits(dados.cpf);
    const telefone = onlyDigits(dados.telefone);
    const cartaoNumero = onlyDigits(dados.cartaoNumero);
    const cartaoNome = (dados.cartaoNome || "").trim();
    const validadeMes = onlyDigits(dados.validadeMes);
    const validadeAno = onlyDigits(dados.validadeAno);
    const ccv = onlyDigits(dados.ccv);

    if (!nome) {
      return failValidation("full_name", "required", "Informe o nome completo do titular.");
    }

    if (!isValidEmail(email)) {
      return failValidation("email", "invalid_email", "Informe um e-mail válido.");
    }

    if (!isValidCpf(cpf)) {
      return failValidation("cpf", "invalid_cpf", "Informe um CPF válido do titular.");
    }

    if (telefone.length < 10) {
      return failValidation("phone", "invalid_phone", "Informe um telefone válido com DDD.");
    }

    if (!cepValido) {
      return failValidation(
        "postal_code",
        "invalid_postal_code",
        "Informe um CEP válido com 8 números antes de continuar.",
        { cep: true }
      );
    }

    if (!numeroEndereco) {
      return failValidation(
        "address_number",
        "required",
        "Informe o número do endereço de cobrança do titular do cartão."
      );
    }

    if (cartaoNumero.length < 13 || cartaoNumero.length > 19) {
      return failValidation("card_number", "invalid_card_number", "Confira o número do cartão.");
    }

    if (!cartaoNome) {
      return failValidation("card_holder", "required", "Informe o nome impresso no cartão.");
    }

    const mes = Number(validadeMes);
    if (!validadeMes || mes < 1 || mes > 12) {
      return failValidation(
        "expiry_month",
        "invalid_expiry_month",
        "Informe um mês de validade válido, de 01 a 12."
      );
    }

    if (validadeAno.length !== 4) {
      return failValidation(
        "expiry_year",
        "invalid_expiry_year",
        "Informe o ano de validade com 4 dígitos."
      );
    }

    const ano = Number(validadeAno);
    const hoje = new Date();
    const mesAtual = hoje.getMonth() + 1;
    const anoAtual = hoje.getFullYear();

    if (ano < anoAtual || (ano === anoAtual && mes < mesAtual)) {
      return failValidation(
        "expiry_date",
        "expired_card",
        `Este cartão venceu em ${validadeMes.padStart(2, "0")}/${validadeAno}. Use outro cartão ou corrija a validade.`
      );
    }

    if (!/^\d{3,4}$/.test(ccv)) {
      return failValidation("security_code", "invalid_security_code", "Confira o código de segurança do cartão.");
    }

    try {
      const body = {
        userId: userId || dados.userId,
        nome,
        email,
        cpf,
        telefone,
        valor: valorPagamento || 299.0,
        parcelas: dados.parcelas,
        endereco: {
          postalCode: cepValido ? cep : "88010000",
          addressNumber: numeroEndereco,
        },
        cartao: {
          holderName: cartaoNome,
          number: cartaoNumero,
          expiryMonth: validadeMes.padStart(2, "0"),
          expiryYear: validadeAno,
          ccv,
        },
        clientSignals: getClientSignals(),
      };

      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("Token não encontrado. Faça login novamente.");
      }

      const resp = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/payments/cartao`,
        body,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (
        resp.status === 200 &&
        resp.data?.success === true &&
        resp.data?.dados?.id
      ) {
        onStageChange?.("completed");
        const paymentId = resp.data.dados.id;
        const amount = Number(resp.data?.dados?.value || body.valor) || Number(body.valor) || 249;
        registrarSalesEvent(userId, "payment_submitted", {
          source: "payment_form",
          metadata: {
            method: "card",
            payment_id: paymentId,
            amount,
            installments: Number(dados.parcelas) || 1,
            provider_status: resp.data?.dados?.status || null,
          },
        });
        registrarSalesEvent(userId, "payment_completed", {
          source: "payment_form",
          metadata: {
            method: "card",
            payment_id: paymentId,
            amount,
            installments: Number(dados.parcelas) || 1,
            provider_status: resp.data?.dados?.status || null,
          },
          keepalive: true,
        });
        if (trackedCheckoutPaymentIdRef.current !== paymentId) {
          trackMetaEvent(
            "InitiateCheckout",
            {
              content_name: "Assinatura Future Trade",
              content_category: "subscription",
              content_ids: ["future-trade-annual"],
              content_type: "product",
              value: Number(body.valor) || 249,
              currency: "BRL",
              num_items: 1,
            },
            `ic:${paymentId}`
          );
          trackMetaPurchase({ paymentId, value: amount });
          trackedCheckoutPaymentIdRef.current = paymentId;
        }
        navigate("/pagamento/sucesso", {
          state: {
            paymentDetails: {
              paymentId,
              amount,
              method: "Cartao",
              purchaseTracked: true,
            },
          },
        });
      } else {
        setErro(resp.data.mensagem || "Falha ao processar o pagamento.");
        registrarSalesEvent(userId, "payment_provider_error", {
          source: "payment_form",
          metadata: { method: "card", code: "unexpected_provider_response" },
        });
      }
    } catch (err) {
      console.error("❌ Erro no pagamento:", err.response?.data || err.message);
      registrarSalesEvent(userId, "payment_provider_error", {
        source: "payment_form",
        metadata: {
          method: "card",
          http_status: err.response?.status || null,
          code: err.response?.data?.code || "card_payment_failed",
        },
      });
      setErro(
        err.response?.data?.mensagem ||
          "Não foi possível concluir o pagamento. Verifique os dados."
      );
    } finally {
      setLoading(false);
    }
  };

  // 🧱 Estilo para campos escuros (tema do painel)
  const darkField = {
    variant: "filled",
    InputProps: {
      sx: {
        backgroundColor: "rgba(255,255,255,0.06)",
        color: "#fff",
        borderRadius: 2,
      },
    },
    InputLabelProps: {
      sx: { color: "rgba(255,255,255,0.72)" },
    },
  };

  return (
    <Box
      sx={{
        mt: 3,
        p: { xs: 2.5, sm: 3.2 },
        borderRadius: 3,
        background: "linear-gradient(180deg, rgba(14,22,32,0.96) 0%, rgba(9,15,24,0.98) 100%)",
        color: "#F1F5FA",
        maxWidth: 600,
        mx: "auto",
        border: "1px solid rgba(255,255,255,0.1)",
        boxShadow: "0 20px 48px rgba(0,0,0,0.35)",
      }}
    >
      <Chip
        label="Pagamento seguro"
        sx={{
          mb: 1.4,
          color: "#CFE9FF",
          bgcolor: "rgba(73, 149, 255, 0.18)",
          border: "1px solid rgba(73,149,255,0.35)",
          fontWeight: 700,
        }}
      />
      <Typography variant="h5" fontWeight={800} mb={0.6} color="#F6FBFF">
        Pagamento com Cartão
      </Typography>
      <Typography sx={{ mb: 2, color: "rgba(241,245,250,0.75)" }}>
        Ambiente protegido para concluir sua assinatura.
      </Typography>

      {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}

      <Box
        sx={{ display: "flex", flexDirection: "column", gap: 2 }}
        onFocusCapture={(event) =>
          markFormStarted(event.target?.name || "unknown")
        }
      >
        <TextField label="Nome completo" name="nome" value={dados.nome} onChange={handleChange} {...darkField} />
        <TextField label="E-mail" name="email" value={dados.email} onChange={handleChange} {...darkField} />
        <TextField label="CPF" name="cpf" value={dados.cpf} onChange={handleChange} placeholder="000.000.000-00" {...darkField} />
        <TextField label="Telefone" name="telefone" value={dados.telefone} onChange={handleChange} placeholder="11999999999" {...darkField} />

        <Typography variant="subtitle1" fontWeight={600} mt={3} color="#fff">
          Dados do Cartão
        </Typography>

        <TextField
          label="CEP de cobrança"
          name="cep"
          value={dados.cep || ""}
          onChange={async (e) => {
            const cep = e.target.value.replace(/\D/g, "");
            if (cep.length <= 8) setDados({ ...dados, cep });

            if (!cep) {
              setErroCep("");
              setDados((prev) => ({ ...prev, cidade: "", uf: "" }));
              return;
            }

            if (cep.length < 8) {
              setErroCep("CEP incompleto (8 números necessários)");
              return;
            }

            if (!/^[0-9]{8}$/.test(cep)) {
              setErroCep("CEP inválido. Use apenas números (ex: 88010000)");
              return;
            }

            if (/^(\d)\1{7}$/.test(cep)) {
              setErroCep("CEP inválido. Digite um CEP real do Brasil.");
              return;
            }

            // Hotfix: não bloquear pagamento por depender do ViaCEP.
            // Se o lookup falhar (rede/CORS), seguimos apenas com a validação de formato.
            setErroCep("");
            try {
              const res = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
              const data = await res.json();
              if (!data?.erro && data?.localidade) {
                setDados((prev) => ({
                  ...prev,
                  cep,
                  cidade: data.localidade,
                  uf: data.uf,
                }));
              }
            } catch {
              // ignora erro de lookup
            }
          }}
          error={!!erroCep}
          helperText={
            erroCep ||
            (dados.cidade
              ? `📍 ${dados.cidade} - ${dados.uf}`
              : "Digite seu CEP com 8 números")
          }
          placeholder="00000-000"
          inputProps={{
            maxLength: 9,
            pattern: "\\d{5}-?\\d{3}",
          }}
          {...darkField}
        />

        <TextField
          label="Número do endereço"
          name="numeroEndereco"
          value={dados.numeroEndereco || ""}
          onChange={handleChange}
          placeholder="Ex: 277"
          {...darkField}
        />

        <TextField
          label="Número do Cartão"
          name="cartaoNumero"
          value={dados.cartaoNumero}
          onChange={handleChange}
          placeholder="**** **** **** ****"
          {...darkField}
        />
        <TextField
          label="Nome impresso no cartão"
          name="cartaoNome"
          value={dados.cartaoNome}
          onChange={handleChange}
          {...darkField}
        />

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr 1fr", sm: "1fr 1fr 1fr" },
            gap: 2,
          }}
        >
          <TextField label="Mês (MM)" name="validadeMes" value={dados.validadeMes} onChange={handleChange} placeholder="06" {...darkField} />
          <TextField label="Ano (AAAA)" name="validadeAno" value={dados.validadeAno} onChange={handleChange} placeholder="2028" {...darkField} />
          <TextField
            label="CCV"
            name="ccv"
            value={dados.ccv}
            onChange={handleChange}
            placeholder="123"
            sx={{ gridColumn: { xs: "1 / -1", sm: "auto" } }}
            {...darkField}
          />
        </Box>

        <TextField
          select
          label="Parcelamento"
          name="parcelas"
          value={dados.parcelas}
          onChange={handleChange}
          {...darkField}
        >
          {[1, 2].map((n) => (
            <MenuItem key={n} value={n}>
              {n}x de {formatCurrencyBRL(getInstallmentAmount(n))}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      <Typography
        variant="caption"
        sx={{
          display: "block",
          mt: 2,
          color: "rgba(241,245,250,0.62)",
          lineHeight: 1.45,
        }}
      >
        Ao pagar, voce confirma ciencia de que o acesso digital sera liberado
        imediatamente apos a confirmacao do pagamento.
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
          mt: 4,
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Button
          variant="contained"
          color="primary"
          fullWidth
          onClick={pagar}
          disabled={
            loading ||
            !/^[0-9]{8}$/.test((dados.cep || "").trim()) ||
            !(dados.numeroEndereco || "").trim()
          }
          sx={{
            background: "linear-gradient(90deg, #2A76E8 0%, #4A95FF 100%)",
            color: "#F7FBFF",
            fontWeight: 800,
            borderRadius: 2,
            textTransform: "none",
            ":hover": { background: "linear-gradient(90deg, #317FF3 0%, #5AA3FF 100%)" },
          }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : "Pagar agora"}
        </Button>

        <Button
          variant="outlined"
          color="secondary"
          fullWidth
          onClick={onCancel}
          sx={{
            color: "rgba(241,245,250,0.82)",
            borderColor: "rgba(255,255,255,0.25)",
            fontWeight: 600,
            borderRadius: 2,
            textTransform: "none",
            ":hover": { borderColor: "rgba(255,255,255,0.42)", color: "#fff" },
          }}
        >
          Voltar
        </Button>
      </Box>
    </Box>
  );
};

export default PagamentoCartao;
